export interface IRect {
    top: number,
    left: number,
    right: number,
    bottom: number
}

/**
 * Определяем, видно ли сейчас элемент.
 */
export function isElementInViewport(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    if (rect.top < 0 || rect.left < 0 ||
        rect.bottom > window.innerHeight ||
        rect.right > window.innerWidth) {
        return false;
    }

    const centerX = (rect.right + rect.left) / 2;
    const centerY = (rect.bottom + rect.top) / 2;

    return (el.contains(document.elementFromPoint(centerX, rect.top)) ||
        el.contains(document.elementFromPoint(rect.right, centerY)) ||
        el.contains(document.elementFromPoint(centerX, rect.bottom)) ||
        el.contains(document.elementFromPoint(rect.left, centerY)));
}

/**
 * Определяем, видно ли сейчас элемент частично. Не проверяет перекрыт ли элемент или нет
 * @param el
 * @param visiblePartThreshold from 0 exclusive to 1 inclusive
 * @param [viewportMargin]
 */
export function isElementPartiallyInViewport(el: HTMLElement, visiblePartThreshold: number, viewportMargin?: Partial<IRect>): boolean {
    if (!visiblePartThreshold) {
        return isElementInViewport(el);
    }

    const viewportRect: IRect = getWindowBorders();

    if (viewportMargin) {
        viewportRect.top += viewportMargin.top || 0;
        viewportRect.left += viewportMargin.left || 0;
        viewportRect.right -= viewportMargin.right || 0;
        viewportRect.bottom -= viewportMargin.bottom || 0;
    }

    const rect = el.getBoundingClientRect();

    const visibleHeight = Math.min(rect.bottom, viewportRect.bottom) - Math.max(viewportRect.top, rect.top);
    const visibleWidth = Math.min(rect.right, viewportRect.right) - Math.max(viewportRect.left, rect.left);

    const elementHeight = el.offsetHeight;
    const elementWidth = el.offsetWidth;

    return elementHeight * visiblePartThreshold <= visibleHeight
            && elementWidth * visiblePartThreshold <= visibleWidth;
}

/**
 *  Проверяем, что элемент полностью попадает во вьюпорт
 * @param el
 */
export function isElementFullInViewport(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();

    return isRectFullInBorders(rect, getWindowBorders());
}

/**
 * Проверка, что элемент полностью находится в клиентской часть элемента-рамки
 * (например если в эелементе есть скролл, то может быть не видно)
 */
export function isElementFullInClientFrame(frame: HTMLElement, el: HTMLElement): boolean {
    return isRectFullInBorders(
        el.getBoundingClientRect(),
        frame.getBoundingClientRect()
    );
}

/**
 * Проверка, что переданные координаты находятся полностью в указанных границах
 */
export function isRectFullInBorders(rect: IRect, borders: IRect): boolean {
    return (
        rect.top >= borders.top &&
        rect.right <= borders.right &&
        rect.left >= borders.left &&
        rect.bottom <= borders.bottom
    );
}

/**
 * Проверка, что элементы и их рамка видны
 * (рамка в окне пользователя, и элемент в клиентской части рамки)
 *
 * @param frame
 * @param els
 * @param [threshold=0.5] - при какой доле площади вхождения считать видимым
 */
export function checkVisibleItemsInFrame(
    frame: HTMLElement,
    els: HTMLElement[],
    threshold = 0.5
): { frame: boolean; els: boolean[] } {
    const frameRect = frame.getBoundingClientRect();
    const winRect = getWindowBorders();

    // Видимая часть находится путем пересечения координат рамки и окна
    const frameVisibleRect = crossRects([frameRect, winRect]);

    const frameVisibleSquare = rectSquare(frameVisibleRect);

    // Если площадь попадания рамки во вьюпорт равна 0, то точто ничего не видно
    if (frameVisibleSquare === 0) {
        return {
            frame: false,
            els: els.map(() => false)
        };
    }

    const frameSquare = rectSquare(frameRect);

    return {
        frame: (frameVisibleSquare > frameSquare * threshold),

        els: els.map((el) => {
            const elRect = el.getBoundingClientRect();

            // Видимая часть находится путем пересечения координат видимой части рамки и элемента
            const elVisibleRect = crossRects([elRect, frameVisibleRect]);

            const elSquare = rectSquare(elRect);
            const elVisibleSquare = rectSquare(elVisibleRect);

            return (elVisibleSquare > elSquare * threshold);
        })
    };
}

/**
 * Область пересечения всех границ
 */
export function crossRects(rects: IRect[]): IRect {
    const res = {
        top: rects[0].top,
        left: rects[0].left,
        right: rects[0].right,
        bottom: rects[0].bottom
    };

    let i = rects.length;
    while (i-- > 1) {
        res.top = Math.max(res.top, rects[i].top);
        res.left = Math.max(res.left, rects[i].left);
        res.right = Math.min(res.right, rects[i].right);
        res.bottom = Math.min(res.bottom, rects[i].bottom);
    }

    return res;
}

/**
 * Площадь области
 */
export function rectSquare(rect: IRect) {
    const {top, left, right, bottom} = rect;

    // Если размеры такие, что облась "отрицательная", то отдаем 0
    if (bottom < top || right < left) {
        return 0;
    }

    return (bottom - top) * (right - left);
}

/**
 * Края окна браузера
 */
export function getWindowBorders(): IRect {
    return {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight
    };
}

/**
 * Значение скролла окна
 */
export function scrollTop(): number {
    return window.pageYOffset || 0;
}

/**
 * Значение скролла окна
 */
export function scrollLeft(): number {
    return window.pageXOffset || 0;
}

/**
 * Значение высоты клиентской области
 */
export function clientHeight(): number {
    return document.documentElement!.clientHeight || 0;
}

/**
 * Значение длинны области прокрутки
 */
export function scrollHeight(): number {
    return document.documentElement!.scrollHeight || 0;
}

/**
 * Сколько осталось до конца прокрутки
 */
export function heightToBottom(): number {
    return scrollHeight() - scrollTop() - clientHeight();
}
